import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Code2, Sparkles, Zap } from 'lucide-react';
import { splitText, animateHeroText, createParallax, fadeInOnScroll, staggerCards } from '../utils/animations';
import { projects } from '../data/mock';

gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  const heroTitleRef = useRef(null);
  const heroSubtitleRef = useRef(null);
  const parallaxRef = useRef(null);
  const featuredProjectsRef = useRef(null);

  useEffect(() => {
    // Split and animate hero text
    if (heroTitleRef.current) {
      const words = heroTitleRef.current.querySelectorAll('.word');
      words.forEach((word) => splitText(word));
      animateHeroText(heroTitleRef.current);
    }

    // Parallax background
    if (parallaxRef.current) {
      createParallax(parallaxRef.current, 0.3);
    }

    // Fade in subtitle
    if (heroSubtitleRef.current) {
      gsap.fromTo(
        heroSubtitleRef.current,
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 1, delay: 1, ease: 'power3.out' }
      );
    }

    // Stagger featured projects
    if (featuredProjectsRef.current) {
      const cards = featuredProjectsRef.current.querySelectorAll('.project-card');
      staggerCards(cards);
    }

    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div className="home-page">
      {/* Hero Section with Parallax */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Parallax Background */}
        <div
          ref={parallaxRef}
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle at 50% 50%, var(--primary) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />

        {/* Floating Elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-[var(--primary)]/10 blur-[120px] rounded-full animate-pulse" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-[var(--secondary)]/10 blur-[150px] rounded-full animate-pulse" style={{ animationDelay: '1s' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          {/* Badge */}
          <div className="mb-8 inline-block">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--secondary-container)] text-[var(--on-secondary-container)] font-body text-xs tracking-code uppercase rounded-sm">
              <Sparkles size={14} />
              Award-Winning Digital Experiences
            </span>
          </div>

          {/* Animated Title */}
          <h1
            ref={heroTitleRef}
            className="font-headline text-6xl md:text-8xl lg:text-9xl font-bold tracking-headline mb-8 leading-[0.9]"
            style={{ perspective: '1000px' }}
          >
            <span className="word block">ENGINEERED</span>
            <span className="word block text-[var(--primary)] text-neon-glow">PRECISION.</span>
          </h1>

          {/* Subtitle */}
          <p
            ref={heroSubtitleRef}
            className="font-body text-[var(--on-surface-variant)] text-xl md:text-2xl max-w-3xl mx-auto mb-12 leading-relaxed"
          >
            We architect digital ecosystems where innovation meets performance.
            <br />
            Building the impossible, one line of code at a time.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/work"
              className="magnetic group bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] px-10 py-4 font-headline font-bold text-sm uppercase tracking-code transition-all duration-300 hover:brightness-110 hover:scale-105 rounded-none inline-flex items-center gap-2"
            >
              View Our Work
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              className="magnetic border-2 border-[var(--outline-variant)]/40 text-[var(--on-surface)] px-10 py-4 font-headline font-bold text-sm uppercase tracking-code hover:bg-[var(--surface-container)] transition-all duration-300 hover:border-[var(--primary)] rounded-none"
            >
              Start a Project
            </Link>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-[var(--primary)]/50 rounded-full flex items-start justify-center p-2">
              <div className="w-1 h-2 bg-[var(--primary)] rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects Preview */}
      <section className="py-32 bg-[var(--surface-container-lowest)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Section Header */}
          <div className="mb-20 text-center">
            <span className="inline-block px-3 py-1 bg-[var(--surface-container-high)] text-[var(--on-surface-variant)] font-body text-xs tracking-code uppercase mb-6 rounded-sm">
              Selected Works
            </span>
            <h2 className="font-headline text-4xl md:text-6xl font-bold tracking-headline uppercase mb-6">
              Featured <span className="text-[var(--primary)]">Projects</span>
            </h2>
            <p className="font-body text-[var(--on-surface-variant)] text-lg max-w-2xl mx-auto">
              A showcase of our most ambitious technical achievements
            </p>
          </div>

          {/* Featured Projects Grid */}
          <div ref={featuredProjectsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.slice(0, 3).map((project) => (
              <Link
                key={project.id}
                to={`/work/${project.id}`}
                className="project-card group relative overflow-hidden bg-[var(--surface-container)] hover:bg-[var(--surface-container-high)] transition-all duration-500"
              >
                {/* Image */}
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"
                  />
                </div>

                {/* Content */}
                <div className="p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-[var(--tertiary)] font-body text-xs tracking-code uppercase">
                      {project.category}
                    </span>
                  </div>
                  <h3 className="font-headline text-2xl font-bold tracking-tight uppercase mb-3">
                    {project.title}
                  </h3>
                  <p className="font-body text-[var(--on-surface-variant)] text-sm mb-6 line-clamp-2">
                    {project.description}
                  </p>
                  <div className="flex items-center gap-2 text-[var(--primary)] font-headline text-sm font-bold uppercase tracking-code group-hover:gap-3 transition-all">
                    View Case Study
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>

                {/* Hover Border */}
                <div className="absolute top-0 left-0 w-full h-0.5 bg-[var(--primary)] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </Link>
            ))}
          </div>

          {/* View All Link */}
          <div className="text-center mt-16">
            <Link
              to="/work"
              className="magnetic inline-flex items-center gap-2 text-[var(--primary)] font-headline text-lg font-bold uppercase tracking-code hover:brightness-110 transition-all group"
            >
              View All Projects
              <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Code2, label: 'Projects Delivered', value: '150+' },
              { icon: Zap, label: 'Years Experience', value: '12+' },
              { icon: Sparkles, label: 'Team Members', value: '45+' },
              { icon: ArrowRight, label: 'Countries Served', value: '28+' },
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="stat-card text-center p-8 bg-[var(--surface-container-low)] hover:bg-[var(--surface-container-high)] transition-all duration-300 group"
                >
                  <div className="flex justify-center mb-4">
                    <Icon
                      size={40}
                      className="text-[var(--primary)] opacity-60 group-hover:opacity-100 transition-opacity"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div className="font-headline text-5xl font-bold text-[var(--on-surface)] mb-2">
                    {stat.value}
                  </div>
                  <div className="font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)]">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
